import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, Upload, Search, Trash2, Download, FolderOpen, FolderPlus,
  File, FileImage, FileSpreadsheet, X, Pencil, Lock,
  Globe, ChevronRight, ExternalLink, Star, Clock, Archive,
  ShieldAlert, History, AlertTriangle,
  Users, Scale, ClipboardCheck, Gavel, Check,
} from 'lucide-react';
import {
  Card, Button, Input, Select, Badge, EmptyState, Modal, Label, Textarea, ConfirmDeleteModal, HelpTooltip,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import {
  listDocuments, listMatters, createDocument, updateDocument, deleteDocument,
  uploadDocumentFile, getDocumentUrl, deleteDocumentFile,
  listFolders, createFolder, updateFolder, deleteFolder,
  setLegalHold, releaseHold,
  listDocumentVersions, createDocumentVersion, logDocumentAccess, listDocumentAccessLog,
  getMyFirmStorageInfo, mapStorageLimitError,
} from '../lib/db';
import { fullName, formatDate } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import DocumentClassificationGuide from '../components/onboarding/DocumentClassificationGuide';

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'pleading',       label: 'Pleading' },
  { value: 'contract',       label: 'Contract' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'evidence',       label: 'Evidence' },
  { value: 'court_order',    label: 'Court Order' },
  { value: 'invoice',        label: 'Invoice' },
  { value: 'retainer',       label: 'Retainer' },
  { value: 'other',          label: 'Other' },
];

const CATEGORY_TONE = {
  pleading: 'red', contract: 'indigo', correspondence: 'blue',
  evidence: 'amber', court_order: 'red', invoice: 'green',
  retainer: 'purple', other: 'slate',
};

// ─── Classification hints — smart nudges keyed by document category ──────────
// Each entry fires a contextual suggestion in the upload/edit flow.
const CATEGORY_HINTS = {
  pleading: {
    suggest: 'privileged',
    title:   'Consider marking as Privileged',
    message: 'Pleadings often contain legal strategy and advice. Marking as Privileged protects attorney-client privilege and creates a clear legal record if disclosure is ever challenged.',
    tone:    'orange',
  },
  correspondence: {
    suggest: 'privileged',
    title:   'Consider marking as Privileged',
    message: 'Client correspondence is typically protected by attorney-client privilege. Marking it as Privileged ensures it is treated correctly if a discovery order or subpoena is ever issued.',
    tone:    'orange',
  },
  retainer: {
    suggest:   'confidential',
    title:     'Consider marking as Confidential',
    message:   'Retainer agreements contain sensitive fee information. Mark as Confidential to restrict access and log all views — or Public if you want the client to see it in their portal.',
    tone:      'amber',
    altSuggest:'public',
    altLabel:  'Make Public (share with client)',
  },
  contract: {
    suggest: 'confidential',
    title:   'Consider marking as Confidential',
    message: 'Contracts often contain commercially sensitive terms. Use Confidential for restricted access, or Privileged if the document also contains legal advice.',
    tone:    'amber',
    altSuggest: 'privileged',
    altLabel: 'Or Privileged (contains legal advice)',
  },
  evidence: {
    suggest: 'confidential',
    title:   'Consider marking as Confidential',
    message: 'Evidence should generally be restricted and access logged. If this is sensitive enough that only named attorneys should see it, use Privileged instead.',
    tone:    'amber',
    altSuggest: 'privileged',
    altLabel:   'Or Privileged (attorneys only)',
  },
  court_order: {
    suggest: 'public',
    title:   'Consider sharing with the client',
    message: 'Court orders are public documents. Marking as Public makes them visible in the client portal so your client can always find their own orders without calling you.',
    tone:    'green',
  },
  invoice: {
    suggest:   'public',
    title:     'Consider sharing with the client',
    message:   'Invoices are usually shared with clients. Marking as Public makes them available in the client portal. Use Confidential instead if this invoice contains sensitive billing details.',
    tone:      'amber',
    altSuggest:'confidential',
    altLabel:  'Or Confidential (sensitive billing)',
  },
};

const FOLDER_COLORS = [
  '#6366f1', '#3b82f6', '#10b981', '#f59e0b',
  '#ef4444', '#8b5cf6', '#ec4899', '#64748b',
];

// Virtual IDs for smart folders (never clash with real UUIDs)
const SMART = {
  RECENT:       '_recent',
  FAVORITES:    '_favorites',
  ARCHIVED:     '_archived',
  LEGAL_HOLD:   '_legal_hold',
  EXPIRING:     '_expiring',
  NEEDS_REVIEW: '_needs_review',
};

const SMART_META = {
  [SMART.RECENT]:       { label: 'Recent',       Icon: Clock,         countKey: '_recent' },
  [SMART.FAVORITES]:    { label: 'Favorites',     Icon: Star,          countKey: '_favorites' },
  [SMART.ARCHIVED]:     { label: 'Archived',      Icon: Archive,       countKey: '_archived' },
  [SMART.LEGAL_HOLD]:   { label: 'Legal Hold',    Icon: Gavel,         countKey: '_legal_hold' },
  [SMART.EXPIRING]:     { label: 'Expiring Soon', Icon: AlertTriangle, countKey: '_expiring' },
  [SMART.NEEDS_REVIEW]: { label: 'Needs Review',  Icon: ClipboardCheck,countKey: '_needs_review' },
};

// ─── Classification tiers ─────────────────────────────────────────────────────
const CLASSIFICATIONS = [
  {
    value: 'public',
    label: 'Public',
    Icon: Globe,
    desc: 'Visible to all, including clients via portal',
    badgeCls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    iconColor: 'text-emerald-500',
    borderCls: 'border-emerald-200 dark:border-emerald-700',
  },
  {
    value: 'internal',
    label: 'Internal',
    Icon: Users,
    desc: 'Firm staff only — not visible in client portal',
    badgeCls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    iconColor: 'text-blue-500',
    borderCls: 'border-blue-200 dark:border-blue-700',
  },
  {
    value: 'confidential',
    label: 'Confidential',
    Icon: Lock,
    desc: 'Restricted access — every view and download is logged',
    badgeCls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    iconColor: 'text-amber-500',
    borderCls: 'border-amber-200 dark:border-amber-700',
  },
  {
    value: 'privileged',
    label: 'Privileged',
    Icon: Scale,
    desc: 'Attorney-client privilege — attorneys only',
    badgeCls: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    iconColor: 'text-orange-500',
    borderCls: 'border-orange-200 dark:border-orange-700',
  },
  {
    value: 'strictly_confidential',
    label: 'Strictly Confidential',
    Icon: ShieldAlert,
    desc: 'Named individuals only — highest restriction',
    badgeCls: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    iconColor: 'text-red-500',
    borderCls: 'border-red-200 dark:border-red-700',
  },
];

const REVIEW_STATUSES = [
  { value: 'draft',      label: 'Draft',      badgeCls: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400' },
  { value: 'in_review',  label: 'In Review',  badgeCls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
  { value: 'approved',   label: 'Approved',   badgeCls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' },
  { value: 'superseded', label: 'Superseded', badgeCls: 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500' },
];

const BLANK_UPLOAD = {
  name: '', description: '', matter_id: '', category: 'other',
  folder_id: '', tags: '', is_confidential: false, portal_visible: false,
  classification: 'internal', review_status: 'draft', is_on_legal_hold: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fileIcon(mime) {
  if (!mime) return File;
  if (mime.startsWith('image/')) return FileImage;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return FileSpreadsheet;
  return FileText;
}

function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function parseTags(val) {
  if (Array.isArray(val)) return val;
  return val ? val.split(',').map((t) => t.trim()).filter(Boolean) : [];
}

function isPreviewable(mime) {
  if (!mime) return false;
  return mime === 'application/pdf' || mime.startsWith('image/');
}

function isSmartId(id) {
  return id && Object.values(SMART).includes(id);
}

// ─── Folder tree helpers ──────────────────────────────────────────────────────
function buildTree(folders, parentId = null) {
  return folders
    .filter((f) => (f.parent_id ?? null) === parentId)
    .map((f) => ({ ...f, children: buildTree(folders, f.id) }));
}

function getDescendantIds(folders, folderId) {
  const ids = [folderId];
  folders.filter((f) => f.parent_id === folderId).forEach((c) => {
    ids.push(...getDescendantIds(folders, c.id));
  });
  return ids;
}

function getFolderPath(folders, folderId) {
  const path = [];
  let current = folders.find((f) => f.id === folderId);
  while (current) {
    path.unshift(current);
    const parentId = current.parent_id;
    current = parentId ? folders.find((f) => f.id === parentId) : null;
  }
  return path;
}

// ─── Folder icon SVG ──────────────────────────────────────────────────────────
function FolderIcon({ color, size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M3 7C3 5.9 3.9 5 5 5H10L12 7H19C20.1 7 21 7.9 21 9V17C21 18.1 20.1 19 19 19H5C3.9 19 3 18.1 3 17V7Z" />
    </svg>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr className="animate-pulse border-b border-slate-100 dark:border-slate-800">
      <td className="w-10 px-4 py-3"><div className="h-3.5 w-3.5 bg-slate-100 dark:bg-slate-700 rounded" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-slate-100 dark:bg-slate-700 rounded w-44" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-slate-100 dark:bg-slate-700 rounded w-20" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-slate-100 dark:bg-slate-700 rounded w-28" /></td>
      <td className="px-4 py-3"><div className="h-5 bg-slate-100 dark:bg-slate-700 rounded-full w-16" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-slate-100 dark:bg-slate-700 rounded w-10" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-slate-100 dark:bg-slate-700 rounded w-24" /></td>
      <td className="px-4 py-3" />
    </tr>
  );
}

// ─── Recursive folder node ────────────────────────────────────────────────────
function FolderNode({ node, level, selected, docCounts, onSelect, onNewSub, onEdit, onDelete, onDrop }) {
  const [expanded, setExpanded] = useState(level === 0);
  const [isDragOver, setIsDragOver] = useState(false);
  const hasChildren = node.children?.length > 0;
  const isActive = selected === node.id;
  const indent = level * 16;

  return (
    <div>
      <div
        className={`group relative flex items-center rounded-lg transition ${isDragOver ? 'ring-2 ring-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragOver(false); onDrop?.(node.id); }}
      >
        {indent > 0 && <div style={{ width: indent }} className="flex-shrink-0" />}

        <button
          onClick={() => setExpanded((v) => !v)}
          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded transition text-slate-400 hover:text-slate-600 ${!hasChildren ? 'invisible' : ''}`}
        >
          <ChevronRight size={12} className={`transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
        </button>

        <button
          onClick={() => onSelect(node.id)}
          className={`flex-1 flex items-center gap-2 px-2 py-2 rounded-lg text-sm transition text-left min-w-0 ${
            isActive
              ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: node.color + '25' }}
          >
            <FolderIcon color={node.color} size={16} />
          </div>
          <span className="flex-1 truncate text-sm">{node.name}</span>
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 group-hover:hidden ${
            isActive
              ? 'bg-indigo-100 dark:bg-indigo-800/50 text-indigo-600 dark:text-indigo-300'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
          }`}>
            {docCounts[node.id] || 0}
          </span>
        </button>

        <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 z-10">
          <button
            onClick={(e) => { e.stopPropagation(); onNewSub(node.id, node.color); }}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600"
            title="Add subfolder"
          ><FolderPlus size={11} /></button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(node); }}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600"
            title="Rename"
          ><Pencil size={11} /></button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(node); }}
            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600"
            title="Delete"
          ><Trash2 size={11} /></button>
        </div>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <FolderNode
              key={child.id}
              node={child}
              level={level + 1}
              selected={selected}
              docCounts={docCounts}
              onSelect={onSelect}
              onNewSub={onNewSub}
              onEdit={onEdit}
              onDelete={onDelete}
              onDrop={onDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Folder sidebar ───────────────────────────────────────────────────────────
function FolderSidebar({ folders, selected, docCounts, onSelect, onNew, onNewSub, onEdit, onDelete, onDrop }) {
  const tree = useMemo(() => buildTree(folders), [folders]);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' | 'flat'

  // Flat list: all folders sorted alphabetically, with breadcrumb path as subtitle
  const flatFolders = useMemo(() =>
    [...folders].sort((a, b) => a.name.localeCompare(b.name)).map((f) => ({
      ...f,
      path: getFolderPath(folders, f.id).map((p) => p.name).join(' / '),
    })),
  [folders]);

  const FolderBtn = ({ id, color, name, count, subtitle }) => {
    const isActive = selected === id;
    return (
      <button
        onClick={() => onSelect(id)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onDrop?.(id); }}
        className={`group w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition text-left ${
          isActive
            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
        }`}
      >
        <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '25' }}>
          <FolderIcon color={color} size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm leading-none">{name}</p>
          {subtitle && <p className="text-[10px] text-slate-400 truncate mt-0.5">{subtitle}</p>}
        </div>
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
          isActive
            ? 'bg-indigo-100 dark:bg-indigo-800/50 text-indigo-600 dark:text-indigo-300'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
        }`}>{count ?? 0}</span>
      </button>
    );
  };

  // On mobile this sidebar stacks above the document grid, so it must
  // go full-width — otherwise the 240px wide column sits next to the
  // content pane on phones and squeezes "Documents live on the matter…"
  // down to one word per line. At lg+ it's the fixed 240px column it
  // was designed for.
  return (
    <div className="w-full lg:w-60 lg:flex-shrink-0 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">

      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Library</p>
        <div className="flex items-center gap-2">
          {/* Tree / flat toggle */}
          <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setViewMode('tree')}
              title="Tree view"
              className={`px-1.5 py-1 transition ${
                viewMode === 'tree'
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {/* Tree icon */}
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="1" width="4" height="3" rx="1" fill="currentColor" opacity=".9"/>
                <rect x="5" y="5.5" width="4" height="3" rx="1" fill="currentColor" opacity=".9"/>
                <rect x="9" y="10" width="4" height="3" rx="1" fill="currentColor" opacity=".9"/>
                <path d="M3 4v2.5h2M7 8.5v2.5h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </button>
            <button
              onClick={() => setViewMode('flat')}
              title="Flat list view"
              className={`px-1.5 py-1 transition ${
                viewMode === 'flat'
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {/* Lines icon */}
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="2" width="12" height="1.5" rx=".75" fill="currentColor"/>
                <rect x="1" y="6.25" width="12" height="1.5" rx=".75" fill="currentColor"/>
                <rect x="1" y="10.5" width="12" height="1.5" rx=".75" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <button
            onClick={() => onNew(null)}
            className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition"
          >
            <FolderPlus size={13} /> New
          </button>
        </div>
      </div>

      <div className="p-2 space-y-0.5">
        {/* All files — always shown */}
        <button
          onClick={() => onSelect(null)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onDrop?.(null); }}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition text-left ${
            selected === null
              ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
            selected === null ? 'bg-indigo-100 dark:bg-indigo-800/40' : 'bg-slate-100 dark:bg-slate-800'
          }`}>
            <FolderOpen size={15} className={selected === null ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400'} />
          </div>
          <span className="flex-1">All files</span>
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${
            selected === null
              ? 'bg-indigo-100 dark:bg-indigo-800/50 text-indigo-600 dark:text-indigo-300'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
          }`}>
            {docCounts._total || 0}
          </span>
        </button>

        {/* Smart folders — standard */}
        <div className="border-t border-slate-100 dark:border-slate-800 mx-1 my-1.5" />
        {[SMART.RECENT, SMART.FAVORITES, SMART.ARCHIVED].map((id) => {
          const { label, Icon, countKey } = SMART_META[id];
          return (
            <button key={id} onClick={() => onSelect(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition text-left ${
                selected === id
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            >
              <Icon size={14} className="flex-shrink-0" />
              <span className="flex-1">{label}</span>
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${
                selected === id
                  ? 'bg-indigo-100 dark:bg-indigo-800/50 text-indigo-600 dark:text-indigo-300'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
              }`}>{docCounts[countKey] || 0}</span>
            </button>
          );
        })}

        {/* Smart folders — compliance */}
        <div className="border-t border-slate-100 dark:border-slate-800 mx-1 my-1.5" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 pb-1">Compliance</p>
        {[SMART.LEGAL_HOLD, SMART.EXPIRING, SMART.NEEDS_REVIEW].map((id) => {
          const { label, Icon, countKey } = SMART_META[id];
          const hasItems = (docCounts[countKey] || 0) > 0;
          return (
            <button key={id} onClick={() => onSelect(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition text-left ${
                selected === id
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            >
              <Icon size={14} className={`flex-shrink-0 ${hasItems && id === SMART.LEGAL_HOLD ? 'text-red-500' : hasItems && id === SMART.EXPIRING ? 'text-amber-500' : ''}`} />
              <span className="flex-1">{label}</span>
              {hasItems && (
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
                  selected === id ? 'bg-indigo-100 dark:bg-indigo-800/50 text-indigo-600'
                  : id === SMART.LEGAL_HOLD ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : id === SMART.EXPIRING  ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>{docCounts[countKey]}</span>
              )}
            </button>
          );
        })}

        {/* Folders section — tree or flat */}
        {folders.length > 0 && <div className="border-t border-slate-100 dark:border-slate-800 mx-1 my-1.5" />}

        {viewMode === 'flat' ? (
          /* ── Flat list ── */
          <div className="space-y-0.5">
            {flatFolders.map((f) => (
              <div key={f.id} className="group relative">
                <FolderBtn
                  id={f.id}
                  color={f.color}
                  name={f.name}
                  count={docCounts[f.id] || 0}
                  subtitle={f.path !== f.name ? f.path : undefined}
                />
                <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 z-10">
                  <button onClick={(e) => { e.stopPropagation(); onNewSub(f.id, f.color); }} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600" title="Add subfolder"><FolderPlus size={11} /></button>
                  <button onClick={(e) => { e.stopPropagation(); onEdit(f); }} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600" title="Rename"><Pencil size={11} /></button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(f); }} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600" title="Delete"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
            {flatFolders.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-slate-400">No folders yet</p>
                <button onClick={() => onNew(null)} className="text-xs text-indigo-600 hover:underline mt-1">Create first folder</button>
              </div>
            )}
          </div>
        ) : (
          /* ── Tree view ── */
          <>
            {tree.map((node) => (
              <FolderNode
                key={node.id}
                node={node}
                level={0}
                selected={selected}
                docCounts={docCounts}
                onSelect={onSelect}
                onNewSub={onNewSub}
                onEdit={onEdit}
                onDelete={onDelete}
                onDrop={onDrop}
              />
            ))}
            {tree.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-slate-400">No folders yet</p>
                <button onClick={() => onNew(null)} className="text-xs text-indigo-600 hover:underline mt-1">Create first folder</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Tag chips ────────────────────────────────────────────────────────────────
function TagChips({ tags, onRemove }) {
  if (!tags?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium">
          {t}
          {onRemove && (
            <button onClick={() => onRemove(t)} className="hover:text-red-500 ml-0.5"><X size={9} /></button>
          )}
        </span>
      ))}
    </div>
  );
}

// ─── Classification badge ─────────────────────────────────────────────────────
function ClassificationBadge({ value, size = 'sm' }) {
  const cls = CLASSIFICATIONS.find((c) => c.value === value) || CLASSIFICATIONS[1];
  const Icon = cls.Icon;
  const sm = size === 'sm';
  // Native browser tooltip explains who can see this tier on hover —
  // matters for new firm members who don't yet know the model. Uses
  // the `desc` field from CLASSIFICATIONS (definitive source above).
  const tooltip = `${cls.label}: ${cls.desc || 'classification tier'}`;
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-1 font-semibold rounded-full cursor-help ${
        sm ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
      } ${cls.badgeCls}`}
    >
      <Icon size={sm ? 9 : 11} />
      {cls.label}
    </span>
  );
}

// ─── Review status badge ──────────────────────────────────────────────────────
function ReviewBadge({ value }) {
  const rs = REVIEW_STATUSES.find((r) => r.value === value);
  if (!rs || value === 'draft') return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${rs.badgeCls}`}>
      <ClipboardCheck size={9} />
      {rs.label}
    </span>
  );
}

// ─── Legal hold pill ──────────────────────────────────────────────────────────
function LegalHoldBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-red-600 text-white">
      <Gavel size={9} />
      Legal Hold
    </span>
  );
}

// ─── Classification hint banner ───────────────────────────────────────────────
// Shows a smart nudge when the selected category has a recommended classification.
// `currentClassification` — what is currently set so we can suppress if already correct.
// `onApply(value)` — called when the user clicks "Apply".
function ClassificationHint({ category, currentClassification, onApply }) {
  const [dismissed, setDismissed] = useState(false);
  const hint = CATEGORY_HINTS[category];

  // Reset dismissed state whenever category changes
  const prevCategory = React.useRef(category);
  if (prevCategory.current !== category) {
    prevCategory.current = category;
    // Can't call setState during render, but dismissed will naturally
    // be stale for one tick — acceptable UX
  }

  if (!hint || dismissed || currentClassification === hint.suggest) return null;

  const suggestedCls  = CLASSIFICATIONS.find((c) => c.value === hint.suggest);
  const altCls        = hint.altSuggest ? CLASSIFICATIONS.find((c) => c.value === hint.altSuggest) : null;

  const tones = {
    orange: {
      wrap:   'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
      text:   'text-orange-800 dark:text-orange-200',
      muted:  'text-orange-600 dark:text-orange-400',
      icon:   'text-orange-500',
      btn:    'bg-orange-100 dark:bg-orange-800/40 hover:bg-orange-200 dark:hover:bg-orange-700/40 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700',
      altBtn: 'text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200',
    },
    amber: {
      wrap:   'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
      text:   'text-amber-800 dark:text-amber-200',
      muted:  'text-amber-600 dark:text-amber-400',
      icon:   'text-amber-500',
      btn:    'bg-amber-100 dark:bg-amber-800/40 hover:bg-amber-200 dark:hover:bg-amber-700/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700',
      altBtn: 'text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200',
    },
    green: {
      wrap:   'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
      text:   'text-emerald-800 dark:text-emerald-200',
      muted:  'text-emerald-600 dark:text-emerald-400',
      icon:   'text-emerald-500',
      btn:    'bg-emerald-100 dark:bg-emerald-800/40 hover:bg-emerald-200 dark:hover:bg-emerald-700/40 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700',
      altBtn: 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200',
    },
  };
  const t = tones[hint.tone] || tones.amber;
  const SuggestIcon = suggestedCls?.Icon;

  return (
    <div className={`rounded-xl border px-3.5 py-3 ${t.wrap}`}>
      <div className="flex items-start gap-2.5">
        <AlertTriangle size={14} className={`flex-shrink-0 mt-0.5 ${t.icon}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold mb-0.5 ${t.text}`}>{hint.title}</p>
          <p className={`text-xs leading-relaxed ${t.muted}`}>{hint.message}</p>
          <div className="flex items-center flex-wrap gap-2 mt-2.5">
            {/* Primary action */}
            <button
              type="button"
              onClick={() => { onApply(hint.suggest); setDismissed(true); }}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition ${t.btn}`}
            >
              {SuggestIcon && <SuggestIcon size={10} />}
              Apply: {suggestedCls?.label}
            </button>
            {/* Alt action */}
            {altCls && (
              <button
                type="button"
                onClick={() => { onApply(hint.altSuggest); setDismissed(true); }}
                className={`text-[11px] font-medium transition ${t.altBtn}`}
              >
                {hint.altLabel}
              </button>
            )}
            {/* Dismiss */}
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className={`ml-auto text-[11px] ${t.muted} hover:opacity-70 transition`}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inline-editable field ────────────────────────────────────────────────────
function InlineSelect({ value, options, onSave, placeholder = 'None' }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <select
        autoFocus
        defaultValue={value || ''}
        onBlur={() => setEditing(false)}
        onChange={(e) => { onSave(e.target.value || null); setEditing(false); }}
        className="text-xs border border-indigo-400 rounded-lg px-2 py-1 bg-white dark:bg-slate-800 dark:text-white outline-none w-full focus:ring-2 focus:ring-indigo-200"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="group/ie flex items-center gap-1.5 text-left w-full rounded hover:bg-slate-50 dark:hover:bg-slate-800/60 -mx-1 px-1 py-0.5 transition"
    >
      <span className="flex-1 min-w-0">{value || <span className="text-slate-400 italic text-xs">{placeholder} — click to set</span>}</span>
      <Pencil size={10} className="opacity-0 group-hover/ie:opacity-100 text-slate-400 flex-shrink-0 transition" />
    </button>
  );
}

// ─── Strictly Confidential allowlist editor ───────────────────────────────────
function AllowlistEditor({ doc, onUpdate, currentUserId }) {
  const { firmId } = useFirm();
  const [firmUsers,  setFirmUsers]  = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [search,     setSearch]     = useState('');
  const allowed = doc.allowed_user_ids || [];

  useEffect(() => {
    if (!firmId) return;
    // Reuse listFirmUsers from db
    import('../lib/db').then(({ listFirmUsers }) => {
      listFirmUsers(firmId).then(({ data }) => setFirmUsers(data || []));
    });
  }, [firmId]);

  const toggle = async (userId) => {
    setSaving(true);
    const next = allowed.includes(userId)
      ? allowed.filter((id) => id !== userId)
      : [...allowed, userId];
    const { data, error } = await updateDocument(doc.id, { allowed_user_ids: next });
    if (!error) {
      onUpdate(data);
      if (currentUserId) {
        logDocumentAccess(doc.id, currentUserId, 'classification_changed', {
          note: `Allowlist updated — ${allowed.includes(userId) ? 'removed' : 'added'} ${userId}`,
        });
      }
    }
    setSaving(false);
  };

  const filtered = firmUsers.filter((u) =>
    !search || `${u.first_name} ${u.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mt-3 rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
      <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 flex items-center gap-2">
        <ShieldAlert size={12} className="text-red-500 flex-shrink-0" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 dark:text-red-400">Named access only</p>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-[11px] text-slate-500 dark:text-slate-400">Only these people can open this document. Admins always have access.</p>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search staff…"
          className="w-full text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-300"
        />
        <div className="space-y-1 max-h-36 overflow-y-auto">
          {filtered.map((u) => {
            const isAllowed = allowed.includes(u.id);
            return (
              <label key={u.id} className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer transition ${isAllowed ? 'bg-red-50 dark:bg-red-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${isAllowed ? 'bg-red-600 border-red-600' : 'border-slate-300 dark:border-slate-600'}`}>
                  {isAllowed && <Check size={9} className="text-white" strokeWidth={3} />}
                </div>
                <input type="checkbox" className="hidden" checked={isAllowed} onChange={() => toggle(u.id)} disabled={saving} />
                <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{u.first_name} {u.last_name}</span>
                {u.role && <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">{u.role}</span>}
              </label>
            );
          })}
          {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No staff found</p>}
        </div>
        {allowed.length === 0 && (
          <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle size={10} /> No one is named — only admins can currently access this.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Preview slide-over ───────────────────────────────────────────────────────
function PreviewPanel({ doc, matters, folders, onClose, onUpdate, onDelete, onTogglePin, onToggleArchive, onToggleLegalHold, onNewVersion, currentUserId }) {
  const [signedUrl,    setSignedUrl]    = useState(null);
  const [urlLoading,   setUrlLoading]   = useState(false);
  const [tagInput,     setTagInput]     = useState('');
  const [versions,     setVersions]     = useState([]);
  const [accessLog,    setAccessLog]    = useState([]);
  const [logLoading,   setLogLoading]   = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showLog,      setShowLog]      = useState(false);
  const versionInputRef = React.useRef();

  // Load signed URL + log a 'view' access event whenever the doc changes
  useEffect(() => {
    if (!doc) { setSignedUrl(null); setVersions([]); setAccessLog([]); return; }
    setUrlLoading(true);
    getDocumentUrl(doc.file_path).then(({ data }) => {
      setSignedUrl(data?.signedUrl || null);
      setUrlLoading(false);
    });
    // Log access
    if (currentUserId) {
      logDocumentAccess(doc.id, currentUserId, 'view', { classification: doc.classification });
    }
    // Keyed on doc.id, not the whole doc — log once per doc, not on
    // every metadata change. currentUserId is captured at access time
    // via closure; if it changes mid-session the next doc open will
    // pick up the new value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id]);

  // Lazy-load version history
  useEffect(() => {
    if (!showVersions || !doc) return;
    listDocumentVersions(doc.id).then(({ data }) => setVersions(data || []));
    // doc.id is the stable key; the rest of the doc object changing
    // shouldn't re-fetch versions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVersions, doc?.id]);

  // Lazy-load access log
  useEffect(() => {
    if (!showLog || !doc) return;
    setLogLoading(true);
    listDocumentAccessLog(doc.id, 30).then(({ data }) => {
      setAccessLog(data || []);
      setLogLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLog, doc?.id]);

  if (!doc) return null;

  const tags = parseTags(doc.tags);
  const canPreview = isPreviewable(doc.mime_type);
  const isImage = doc.mime_type?.startsWith('image/');
  const FileIcon = fileIcon(doc.mime_type);
  const fileExt = doc.name?.includes('.')
    ? doc.name.split('.').pop().toUpperCase()
    : doc.mime_type?.split('/').pop()?.toUpperCase() || 'FILE';

  const patch = async (fields) => {
    const { data, error } = await updateDocument(doc.id, fields);
    if (error) { toast.error(error.message); return; }
    onUpdate(data);
    // Log classification changes
    if (fields.classification && fields.classification !== doc.classification && currentUserId) {
      logDocumentAccess(doc.id, currentUserId, 'classification_changed', {
        from: doc.classification, to: fields.classification,
      });
    }
    // Log portal share toggle
    if (fields.portal_visible !== undefined && currentUserId) {
      logDocumentAccess(doc.id, currentUserId, 'share', { portal_visible: fields.portal_visible });
    }
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) { setTagInput(''); return; }
    setTagInput('');
    await patch({ tags: [...tags, t] });
  };

  const matterOptions = (matters || []).map((m) => ({
    value: m.id,
    label: `${m.matter_number ? m.matter_number + ' · ' : ''}${m.title}`,
  }));
  const folderOptions = (folders || []).map((f) => ({ value: f.id, label: f.name }));

  const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">{children}</p>
  );

  return (
    <div className="fixed inset-0 z-40 flex" onClick={onClose}>
      <div className="flex-1 bg-black/20 dark:bg-black/40" />
      <div
        className="w-[420px] max-w-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col h-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
          <div className="flex items-start gap-3">
            {/* File type icon box */}
            <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
              <FileIcon size={22} className="text-indigo-600 dark:text-indigo-400" />
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[15px] leading-snug text-slate-900 dark:text-white truncate">{doc.name}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 uppercase tracking-wider">{fileExt}</span>
                <span className="text-xs text-slate-400">{formatBytes(doc.file_size)}</span>
                <span className="text-slate-200 dark:text-slate-700">·</span>
                <span className="text-xs text-slate-400">{formatDate(doc.created_at)}</span>
              </div>
            </div>

            {/* Top-right actions */}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={() => onTogglePin(doc)}
                title={doc.is_pinned ? 'Unpin' : 'Pin to Favorites'}
                className={`p-1.5 rounded-lg transition ${doc.is_pinned ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-amber-500'}`}
              >
                <Star size={15} fill={doc.is_pinned ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={() => onToggleArchive(doc)}
                title={doc.is_archived ? 'Unarchive' : 'Archive'}
                className={`p-1.5 rounded-lg transition ${doc.is_archived ? 'text-slate-700 bg-slate-100 dark:bg-slate-800' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <Archive size={15} />
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Security tier + review + hold */}
          <div className="mt-3 space-y-2">
            {/* Classification selector */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Classification</span>
                <ClassificationBadge value={doc.classification || 'internal'} />
              </div>
              <div className="grid grid-cols-5 divide-x divide-slate-100 dark:divide-slate-800">
                {CLASSIFICATIONS.map((cls) => {
                  const Icon = cls.Icon;
                  const isActive = (doc.classification || 'internal') === cls.value;
                  return (
                    <button
                      key={cls.value}
                      title={`${cls.label}: ${cls.desc}`}
                      onClick={() => patch({ classification: cls.value, is_confidential: ['confidential','privileged','strictly_confidential'].includes(cls.value) })}
                      className={`flex flex-col items-center gap-1 py-2 transition ${
                        isActive ? cls.badgeCls : 'text-slate-300 dark:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                      }`}
                    >
                      <Icon size={13} />
                      <span className="text-[8px] font-bold leading-none text-center px-0.5 leading-tight">{cls.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </div>
              <p className="px-3 py-1.5 text-[10px] text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800">
                {CLASSIFICATIONS.find((c) => c.value === (doc.classification || 'internal'))?.desc}
              </p>
            </div>

            {/* Review status + client share row */}
            <div className="flex gap-2">
              <select
                value={doc.review_status || 'draft'}
                onChange={(e) => patch({ review_status: e.target.value })}
                className="flex-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                {REVIEW_STATUSES.map((rs) => <option key={rs.value} value={rs.value}>{rs.label}</option>)}
              </select>
              <button
                onClick={() => patch({ portal_visible: !doc.portal_visible })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                  doc.portal_visible
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-emerald-300 hover:text-emerald-600'
                }`}
              >
                <Globe size={11} />
                {doc.portal_visible ? 'Shared' : 'Share'}
              </button>
            </div>

            {/* Legal hold alert */}
            {doc.is_on_legal_hold && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <Gavel size={13} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-red-700 dark:text-red-300">On Legal Hold</p>
                  {doc.legal_hold_reason && <p className="text-[10px] text-red-600 dark:text-red-400 truncate">{doc.legal_hold_reason}</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Preview ── */}
        <div className="flex-shrink-0 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-center" style={{ height: 200 }}>
          {urlLoading ? (
            <div className="animate-pulse flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-2xl bg-slate-200 dark:bg-slate-700" />
              <div className="h-3 w-24 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
          ) : canPreview && signedUrl ? (
            isImage
              ? <img src={signedUrl} alt={doc.name} className="max-h-full max-w-full object-contain" />
              : <iframe src={signedUrl} title={doc.name} className="w-full h-full border-0" />
          ) : (
            /* Improved no-preview state */
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 flex items-center justify-center mx-auto mb-3">
                <FileIcon size={26} className="text-indigo-500" />
              </div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">{fileExt} — no preview</p>
              <div className="flex gap-2 justify-center">
                {signedUrl && (
                  <button
                    onClick={() => window.open(signedUrl, '_blank')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition"
                  >
                    <ExternalLink size={11} /> Open file
                  </button>
                )}
                <button
                  onClick={() => signedUrl && window.open(signedUrl, '_blank')}
                  disabled={!signedUrl}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition disabled:opacity-40"
                >
                  <Download size={11} /> Download
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">

          {/* DETAILS */}
          <div className="px-5 py-4">
            <SectionLabel>Details</SectionLabel>
            <dl className="space-y-3">

              {/* Category — inline editable */}
              <div className="flex items-center gap-2">
                <dt className="text-xs text-slate-400 w-20 flex-shrink-0">Category</dt>
                <dd className="flex-1 min-w-0">
                  <InlineSelect
                    value={<Badge tone={CATEGORY_TONE[doc.category] || 'slate'} className="pointer-events-none">
                      {CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category}
                    </Badge>}
                    options={CATEGORIES}
                    onSave={(val) => patch({ category: val || 'other' })}
                    placeholder="Select category"
                  />
                </dd>
              </div>

              {/* Case file — inline editable */}
              <div className="flex items-center gap-2">
                <dt className="text-xs text-slate-400 w-20 flex-shrink-0">Case file</dt>
                <dd className="flex-1 min-w-0">
                  <InlineSelect
                    value={doc.matter ? (
                      <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
                        {doc.matter.matter_number && <span className="font-medium">{doc.matter.matter_number} · </span>}
                        {doc.matter.title}
                      </span>
                    ) : null}
                    options={matterOptions}
                    onSave={(val) => patch({ matter_id: val || null })}
                    placeholder="Assign case file"
                  />
                </dd>
              </div>

              {/* Folder — inline editable */}
              <div className="flex items-center gap-2">
                <dt className="text-xs text-slate-400 w-20 flex-shrink-0">Folder</dt>
                <dd className="flex-1 min-w-0">
                  <InlineSelect
                    value={doc.folder ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: doc.folder.color }} />
                        <span className="text-sm text-slate-700 dark:text-slate-200">{doc.folder.name}</span>
                      </span>
                    ) : null}
                    options={folderOptions}
                    onSave={(val) => patch({ folder_id: val || null })}
                    placeholder="Move to folder"
                  />
                </dd>
              </div>

              {/* Owner — read-only */}
              {doc.uploaded_by && (
                <div className="flex items-center gap-2">
                  <dt className="text-xs text-slate-400 w-20 flex-shrink-0">Owner</dt>
                  <dd className="text-sm text-slate-600 dark:text-slate-300">{fullName(doc.uploaded_by) || '—'}</dd>
                </div>
              )}

              {/* Expiry — read-only */}
              {doc.expires_at && (
                <div className="flex items-center gap-2">
                  <dt className="text-xs text-slate-400 w-20 flex-shrink-0">Expires</dt>
                  <dd className={`text-sm font-medium ${new Date(doc.expires_at) < new Date() ? 'text-red-600' : 'text-slate-600 dark:text-slate-300'}`}>
                    {formatDate(doc.expires_at)}{new Date(doc.expires_at) < new Date() && ' · Expired'}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* TAGS */}
          <div className="px-5 py-4">
            <SectionLabel>Tags</SectionLabel>
            <TagChips tags={parseTags(doc.tags)} onRemove={(t) => patch({ tags: tags.filter((x) => x !== t) })} />
            <div className="flex gap-2 mt-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTag()}
                placeholder="Type a tag and press Enter…"
                className="flex-1 text-xs"
              />
              <Button size="sm" variant="secondary" onClick={addTag} disabled={!tagInput.trim()}>Add</Button>
            </div>
          </div>

          {/* DESCRIPTION */}
          {doc.description && (
            <div className="px-5 py-4">
              <SectionLabel>Description</SectionLabel>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{doc.description}</p>
            </div>
          )}

          {/* SECURITY DETAILS */}
          <div className="px-5 py-4">
            <SectionLabel>Security details</SectionLabel>
            <dl className="space-y-2.5">
              <div className="flex items-center gap-2">
                <dt className="text-xs text-slate-400 w-24 flex-shrink-0">Version</dt>
                <dd className="text-xs font-semibold text-slate-700 dark:text-slate-200">v{doc.version_number || 1}</dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="text-xs text-slate-400 w-24 flex-shrink-0">Review</dt>
                <dd><ReviewBadge value={doc.review_status || 'draft'} /></dd>
              </div>
              {doc.reviewed_by && doc.reviewed_at && (
                <div className="flex items-center gap-2">
                  <dt className="text-xs text-slate-400 w-24 flex-shrink-0">Approved by</dt>
                  <dd className="text-xs text-slate-600 dark:text-slate-300">{fullName(doc.reviewed_by)} · {formatDate(doc.reviewed_at)}</dd>
                </div>
              )}
              <div className="flex items-center gap-2">
                <dt className="text-xs text-slate-400 w-24 flex-shrink-0">Legal hold</dt>
                <dd>
                  {doc.is_on_legal_hold
                    ? <LegalHoldBadge />
                    : <span className="text-xs text-slate-400">None</span>
                  }
                </dd>
              </div>
            </dl>

            {/* Strictly Confidential allowlist */}
            {(doc.classification === 'strictly_confidential') && (
              <AllowlistEditor
                doc={doc}
                onUpdate={onUpdate}
                currentUserId={currentUserId}
              />
            )}
          </div>

          {/* ACTIVITY */}
          <div className="px-5 py-4">
            <SectionLabel>Activity</SectionLabel>
            <div className="space-y-2.5">
              <div className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0 mt-1.5" />
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-300">
                    Uploaded {doc.uploaded_by ? <>by <span className="font-medium">{fullName(doc.uploaded_by)}</span></> : ''}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{formatDate(doc.created_at)}</p>
                </div>
              </div>
              {doc.is_pinned && (
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 mt-1.5" />
                  <p className="text-xs text-slate-500">Pinned to Favorites</p>
                </div>
              )}
              {doc.is_archived && (
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0 mt-1.5" />
                  <p className="text-xs text-slate-500">Archived</p>
                </div>
              )}
              {doc.is_on_legal_hold && (
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                  <div>
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium">Legal hold applied</p>
                    {doc.legal_hold_reason && <p className="text-[10px] text-slate-400">{doc.legal_hold_reason}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* VERSION HISTORY */}
          <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setShowVersions((v) => !v)}
              className="w-full flex items-center justify-between text-left group"
            >
              <SectionLabel>Version history</SectionLabel>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 group-hover:text-indigo-600 transition">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">v{doc.version_number || 1}</span>
                <ChevronRight size={13} className={`transition-transform ${showVersions ? 'rotate-90' : ''}`} />
              </div>
            </button>
            {showVersions && (
              <div className="mt-2 space-y-1.5">
                {versions.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No version history yet. Upload a new version using the <History size={11} className="inline" /> button below.</p>
                ) : (
                  versions.map((v) => (
                    <div key={v.id} className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg ${v.version_number === doc.version_number ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-slate-50 dark:bg-slate-800/40'}`}>
                      <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${v.version_number === doc.version_number ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                        v{v.version_number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-700 dark:text-slate-200 font-medium truncate">
                          {v.version_number === doc.version_number ? 'Current' : `v${v.version_number}`}
                          {v.change_notes && <span className="font-normal text-slate-400"> · {v.change_notes}</span>}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {v.uploaded_by ? fullName(v.uploaded_by) : 'Unknown'} · {formatDate(v.created_at)} · {formatBytes(v.file_size)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ACCESS LOG */}
          <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setShowLog((v) => !v)}
              className="w-full flex items-center justify-between text-left group"
            >
              <SectionLabel>Access log</SectionLabel>
              <ChevronRight size={13} className={`text-slate-400 group-hover:text-indigo-600 transition-transform ${showLog ? 'rotate-90' : ''}`} />
            </button>
            {showLog && (
              <div className="mt-2 space-y-1.5">
                {logLoading ? (
                  <div className="space-y-1.5">
                    {[...Array(3)].map((_, i) => <div key={i} className="h-8 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />)}
                  </div>
                ) : accessLog.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No access events recorded yet.</p>
                ) : (
                  accessLog.map((entry) => {
                    const ACTION_LABELS = {
                      view: 'Viewed',
                      download: 'Downloaded',
                      edit: 'Edited',
                      share: 'Sharing changed',
                      hold_applied: 'Legal hold applied',
                      hold_released: 'Legal hold released',
                      approved: 'Approved',
                      superseded: 'Marked superseded',
                      classification_changed: 'Classification changed',
                      version_uploaded: 'New version uploaded',
                    };
                    const ACTION_COLORS = {
                      download: 'bg-blue-400',
                      hold_applied: 'bg-red-400',
                      hold_released: 'bg-emerald-400',
                      approved: 'bg-emerald-400',
                      classification_changed: 'bg-amber-400',
                      version_uploaded: 'bg-indigo-400',
                    };
                    const dotColor = ACTION_COLORS[entry.action] || 'bg-slate-300';
                    return (
                      <div key={entry.id} className="flex items-start gap-2.5">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${dotColor}`} />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            <span className="font-medium">{ACTION_LABELS[entry.action] || entry.action}</span>
                            {entry.user && <> by {fullName(entry.user)}</>}
                          </p>
                          {entry.metadata?.from && (
                            <p className="text-[10px] text-slate-400">{entry.metadata.from} → {entry.metadata.to}</p>
                          )}
                          <p className="text-[10px] text-slate-400">{formatDate(entry.accessed_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer — sticky, compact ── */}
        {/* Hidden file input for new version upload */}
        <input ref={versionInputRef} type="file" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onNewVersion(doc, f); e.target.value = ''; }}
        />
        <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 dark:border-slate-800 flex-shrink-0 bg-white dark:bg-slate-900 flex-wrap">
          <Button variant="secondary" icon={Download}
            onClick={() => {
              if (signedUrl) {
                window.open(signedUrl, '_blank');
                if (currentUserId) logDocumentAccess(doc.id, currentUserId, 'download', { version: doc.version_number });
              }
            }}
            disabled={!signedUrl} className="flex-1">
            Download
          </Button>
          {signedUrl && (
            <Button variant="ghost" onClick={() => window.open(signedUrl, '_blank')} title="Open in new tab">
              <ExternalLink size={15} />
            </Button>
          )}
          {/* Upload new version */}
          <button
            onClick={() => versionInputRef.current?.click()}
            title="Upload new version"
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 hover:border-indigo-400 hover:text-indigo-600 transition"
          >
            <History size={15} />
          </button>
          <button
            onClick={() => onToggleLegalHold(doc)}
            title={doc.is_on_legal_hold ? 'Release legal hold' : 'Apply legal hold'}
            className={`p-2 rounded-lg border transition ${
              doc.is_on_legal_hold
                ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400'
                : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:border-red-300 hover:text-red-600'
            }`}
          >
            <Gavel size={15} />
          </button>
          <Button variant="danger" icon={Trash2} onClick={() => onDelete(doc)} disabled={doc.is_on_legal_hold}>Delete</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Upload drop zone ─────────────────────────────────────────────────────────
function UploadZone({ onFiles }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); const f = Array.from(e.dataTransfer.files); if (f.length) onFiles(f); }}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
        dragging
          ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/30'
          : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-800/40'
      }`}
    >
      <Upload size={28} className="mx-auto mb-2 text-slate-400" />
      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Drop files here or <span className="text-indigo-600">browse</span></p>
      <p className="text-xs text-slate-400 mt-1">PDF, Word, Excel, images — max 50 MB per file</p>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => onFiles(Array.from(e.target.files))} />
    </div>
  );
}

// ─── Folder modal ─────────────────────────────────────────────────────────────
function FolderModal({ open, target, parentId, parentFolder, firmId, onSaved, onClose }) {
  const [form, setForm] = useState({ name: '', color: FOLDER_COLORS[0] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(target
        ? { name: target.name, color: target.color }
        : { name: '', color: parentFolder?.color || FOLDER_COLORS[0] }
      );
    }
  }, [open, target, parentFolder]);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (target) {
      const { data, error } = await updateFolder(target.id, form);
      if (!error) onSaved(data, true); else toast.error(error.message);
    } else {
      const { data, error } = await createFolder(firmId, { ...form, parent_id: parentId || null });
      if (!error) onSaved(data, false); else toast.error(error.message);
    }
    setSaving(false);
  };

  const title = target ? 'Rename folder' : parentFolder ? `New subfolder in "${parentFolder.name}"` : 'New folder';

  return (
    <Modal open={open} onClose={onClose} title={title} footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || !form.name.trim()}>
          {saving ? 'Saving…' : target ? 'Save' : parentId ? 'Create subfolder' : 'Create folder'}
        </Button>
      </>
    }>
      <div className="space-y-4">
        <div>
          <Label>Folder name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Court Filings" autoFocus />
        </div>
        <div>
          <Label>Colour</Label>
          <div className="flex gap-2 mt-1">
            {FOLDER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setForm({ ...form, color: c })}
                className="w-7 h-7 rounded-full border-2 transition"
                style={{ backgroundColor: c, borderColor: form.color === c ? '#1e293b' : 'transparent' }}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Bulk action bar ──────────────────────────────────────────────────────────
function BulkActionBar({ count, onPin, onArchive, onMove, onDelete, onClear }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-indigo-600 dark:bg-indigo-700 rounded-xl text-white text-sm shadow-lg">
      <span className="font-semibold whitespace-nowrap">{count} selected</span>
      <div className="flex-1 flex items-center gap-1 flex-wrap">
        <button
          onClick={onPin}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-white/20 transition"
        >
          <Star size={12} /> Pin
        </button>
        <button
          onClick={onArchive}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-white/20 transition"
        >
          <Archive size={12} /> Archive
        </button>
        <button
          onClick={onMove}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-white/20 transition"
        >
          <FolderOpen size={12} /> Move to…
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-red-500/70 transition"
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
      <button onClick={onClear} className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white flex-shrink-0">
        <X size={15} />
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function DocumentsPage() {
  const { firmId, refreshSignal } = useFirm();
  const { userProfile } = useAuth();
  const log = useActivityLogger();

  const [documents,  setDocuments]  = useState([]);
  const [folders,    setFolders]    = useState([]);
  const [matters,    setMatters]    = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Folder selection / filters
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [q,              setQ]              = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter,      setTagFilter]      = useState('');
  const [confidFilter,   setConfidFilter]   = useState('');

  // Preview
  const [previewDoc, setPreviewDoc] = useState(null);

  // Edit modal
  const [editDoc,    setEditDoc]    = useState(null);
  const [editForm,   setEditForm]   = useState({
    name: '', description: '', category: 'other', matter_id: '',
    folder_id: '', tags: '', expires_at: '', is_confidential: false, portal_visible: false,
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Folder modal
  const [folderModal,    setFolderModal]    = useState(false);
  const [editingFolder,  setEditingFolder]  = useState(null);
  const [newSubParentId, setNewSubParentId] = useState(null);

  // Upload modal
  const [showUpload,     setShowUpload]     = useState(false);
  const [pendingFiles,   setPendingFiles]   = useState([]);
  const [fileMeta,       setFileMeta]       = useState({});
  const [uploading,      setUploading]      = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [bulkDeleteCount, setBulkDeleteCount] = useState(0);

  // Inline rename
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal,  setRenameVal]  = useState('');

  // Drag-to-folder
  const [draggingDocId, setDraggingDocId] = useState(null);

  // Bulk move modal
  const [bulkMoveOpen,     setBulkMoveOpen]     = useState(false);
  const [bulkMoveFolderId, setBulkMoveFolderId] = useState('');

  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  // Storage usage info — fetched on mount, refreshed when docs change
  // (after upload/delete). Drives the inline storage chip in the
  // header so users always see how close they are to their cap.
  const [storageInfo, setStorageInfo] = useState(null);

  // ── Load data ──
  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    Promise.all([
      listDocuments(firmId),
      listFolders(firmId),
      listMatters(firmId),
      getMyFirmStorageInfo(),
    ]).then(([{ data: docs }, { data: flds }, { data: m }, { data: storage }]) => {
      setDocuments(docs || []);
      setFolders(flds || []);
      setMatters(m || []);
      setStorageInfo(storage || null);
    }).catch(() => {
      // Timeout or network error — show empty state rather than spinning forever
    }).finally(() => setLoading(false));
  }, [firmId, refreshSignal]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (renamingId) { setRenamingId(null); return; }
        if (previewDoc) { setPreviewDoc(null); return; }
        if (selectedIds.size) { setSelectedIds(new Set()); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [renamingId, previewDoc, selectedIds]);

  // ── Derived counts ──
  const docCounts = useMemo(() => {
    const nonArchived = documents.filter((d) => !d.is_archived);
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysOut = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    const counts = {
      _total:        nonArchived.length,
      _recent:       nonArchived.filter((d) => new Date(d.created_at) >= sevenDaysAgo).length,
      _favorites:    nonArchived.filter((d) => d.is_pinned).length,
      _archived:     documents.filter((d) => d.is_archived).length,
      _legal_hold:   nonArchived.filter((d) => d.is_on_legal_hold).length,
      _expiring:     nonArchived.filter((d) => d.expires_at && new Date(d.expires_at) <= thirtyDaysOut && new Date(d.expires_at) >= new Date()).length,
      _needs_review: nonArchived.filter((d) => d.review_status === 'in_review').length,
    };
    folders.forEach((f) => {
      const ids = new Set(getDescendantIds(folders, f.id));
      counts[f.id] = nonArchived.filter((d) => d.folder_id && ids.has(d.folder_id)).length;
    });
    return counts;
  }, [documents, folders]);

  // All tags (non-archived)
  const allTags = useMemo(() => {
    const set = new Set();
    documents.filter((d) => !d.is_archived).forEach((d) => parseTags(d.tags).forEach((t) => set.add(t)));
    return [...set].sort();
  }, [documents]);

  // Descendant folder ids for selected regular folder
  const selectedFolderIds = useMemo(() => {
    if (!selectedFolder || isSmartId(selectedFolder)) return null;
    return new Set(getDescendantIds(folders, selectedFolder));
  }, [folders, selectedFolder]);

  // Filtered documents
  const filtered = useMemo(() => {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    let base = documents;

    const thirtyDaysOut = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    if (selectedFolder === SMART.ARCHIVED) {
      base = base.filter((d) => d.is_archived);
    } else if (selectedFolder === SMART.FAVORITES) {
      base = base.filter((d) => d.is_pinned && !d.is_archived);
    } else if (selectedFolder === SMART.RECENT) {
      base = base.filter((d) => !d.is_archived && new Date(d.created_at) >= sevenDaysAgo);
    } else if (selectedFolder === SMART.LEGAL_HOLD) {
      base = base.filter((d) => d.is_on_legal_hold && !d.is_archived);
    } else if (selectedFolder === SMART.EXPIRING) {
      base = base.filter((d) => !d.is_archived && d.expires_at && new Date(d.expires_at) <= thirtyDaysOut && new Date(d.expires_at) >= new Date());
    } else if (selectedFolder === SMART.NEEDS_REVIEW) {
      base = base.filter((d) => !d.is_archived && d.review_status === 'in_review');
    } else if (selectedFolder === null) {
      base = base.filter((d) => !d.is_archived);
    } else {
      base = base.filter((d) => !d.is_archived && selectedFolderIds?.has(d.folder_id));
    }

    return base
      .filter((d) => !categoryFilter || d.category === categoryFilter)
      .filter((d) => !confidFilter   || String(d.is_confidential) === confidFilter)
      .filter((d) => !tagFilter      || parseTags(d.tags).includes(tagFilter))
      .filter((d) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (
          d.name?.toLowerCase().includes(s) ||
          d.description?.toLowerCase().includes(s) ||
          d.matter?.title?.toLowerCase().includes(s) ||
          d.matter?.matter_number?.toLowerCase().includes(s) ||
          parseTags(d.tags).some((t) => t.toLowerCase().includes(s))
        );
      });
  }, [documents, selectedFolder, selectedFolderIds, categoryFilter, confidFilter, tagFilter, q]);

  // ── Handlers ──
  const handleDocUpdate = (updated) => {
    setDocuments((prev) => prev.map((d) => d.id === updated.id ? { ...d, ...updated } : d));
    if (previewDoc?.id === updated.id) setPreviewDoc((p) => ({ ...p, ...updated }));
  };

  const openEdit = (doc, e) => {
    e?.stopPropagation();
    setEditForm({
      name:            doc.name            || '',
      description:     doc.description     || '',
      category:        doc.category        || 'other',
      matter_id:       doc.matter_id       || '',
      folder_id:       doc.folder_id       || '',
      tags:            parseTags(doc.tags).join(', '),
      is_confidential: doc.is_confidential || false,
      portal_visible:  doc.portal_visible  || false,
      expires_at:      doc.expires_at      || '',
      classification:  doc.classification  || 'internal',
      review_status:   doc.review_status   || 'draft',
    });
    setEditDoc(doc);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return;
    setSavingEdit(true);
    const { data, error } = await updateDocument(editDoc.id, {
      name:            editForm.name.trim(),
      description:     editForm.description || null,
      category:        editForm.category,
      matter_id:       editForm.matter_id   || null,
      folder_id:       editForm.folder_id   || null,
      tags:            parseTags(editForm.tags),
      is_confidential: editForm.is_confidential,
      portal_visible:  editForm.portal_visible,
      expires_at:      editForm.expires_at  || null,
      classification:  editForm.classification  || 'internal',
      review_status:   editForm.review_status   || 'draft',
    });
    setSavingEdit(false);
    if (error) { toast.error(error.message); return; }
    // Log the edit and any classification change
    if (userProfile?.id) {
      logDocumentAccess(editDoc.id, userProfile.id, 'edit', {});
      if (editForm.classification !== editDoc.classification) {
        logDocumentAccess(editDoc.id, userProfile.id, 'classification_changed', {
          from: editDoc.classification, to: editForm.classification,
        });
      }
    }
    handleDocUpdate(data);
    setEditDoc(null);
  };

  const handleDownload = async (doc) => {
    const { data, error } = await getDocumentUrl(doc.file_path);
    if (error || !data?.signedUrl) { toast.error('Could not generate download link.'); return; }
    window.open(data.signedUrl, '_blank');
    if (userProfile?.id) {
      logDocumentAccess(doc.id, userProfile.id, 'download', { version: doc.version_number });
    }
  };

  const handleDeleteConfirm = async () => {
    await deleteDocumentFile(deletingDoc.file_path);
    await deleteDocument(deletingDoc.id);
    setDocuments((prev) => prev.filter((d) => d.id !== deletingDoc.id));
    setSelectedIds((prev) => { const s = new Set(prev); s.delete(deletingDoc.id); return s; });
    if (previewDoc?.id === deletingDoc.id) setPreviewDoc(null);
    setDeletingDoc(null);
  };

  const handleDelete = (doc) => {
    setDeletingDoc(doc);
  };

  // Inline rename
  const startRename = (doc, e) => {
    e.stopPropagation();
    setRenamingId(doc.id);
    setRenameVal(doc.name);
  };

  const commitRename = async () => {
    if (!renameVal.trim() || !renamingId) { setRenamingId(null); return; }
    const { data } = await updateDocument(renamingId, { name: renameVal.trim() });
    if (data) setDocuments((prev) => prev.map((d) => d.id === renamingId ? { ...d, ...data } : d));
    setRenamingId(null);
  };

  // Pin / archive
  const togglePin = async (doc) => {
    const { data } = await updateDocument(doc.id, { is_pinned: !doc.is_pinned });
    if (data) handleDocUpdate(data);
  };

  const toggleLegalHold = async (doc) => {
    if (doc.is_on_legal_hold) {
      const { data } = await releaseHold(doc.id);
      if (data) {
        handleDocUpdate(data);
        if (userProfile?.id) logDocumentAccess(doc.id, userProfile.id, 'hold_released', {});
      }
    } else {
      const reason = window.prompt('Enter reason for legal hold (optional):') ?? '';
      const { data } = await setLegalHold(doc.id, { reason, userId: userProfile?.id });
      if (data) {
        handleDocUpdate(data);
        if (userProfile?.id) logDocumentAccess(doc.id, userProfile.id, 'hold_applied', { reason });
      }
    }
  };

  const handleNewVersion = async (doc, file) => {
    if (file.size > 50 * 1024 * 1024) { toast.warning('File too large — max 50 MB.'); return; }
    const versionNotes = window.prompt(`Upload new version of "${doc.name}". Add change notes (optional):`) ?? '';
    // Save current version to history first
    await createDocumentVersion({
      document_id:    doc.id,
      version_number: doc.version_number || 1,
      file_path:      doc.file_path,
      file_size:      doc.file_size,
      mime_type:      doc.mime_type,
      uploaded_by:    doc.uploaded_by?.id || null,
      change_notes:   null,
    });
    // Upload new file
    const { path, error: uploadErr } = await uploadDocumentFile(firmId, file);
    if (uploadErr) { toast.error('Upload failed: ' + uploadErr.message); return; }
    const newVersion = (doc.version_number || 1) + 1;
    const { data, error } = await updateDocument(doc.id, {
      file_path:      path,
      file_size:      file.size,
      mime_type:      file.type,
      version_number: newVersion,
    });
    if (error) { toast.error('Failed to update document record: ' + error.message); return; }
    handleDocUpdate(data);
    // Log the version upload
    if (userProfile?.id) {
      logDocumentAccess(doc.id, userProfile.id, 'version_uploaded', {
        version: newVersion, notes: versionNotes,
      });
    }
    // Save new version record
    await createDocumentVersion({
      document_id:    doc.id,
      version_number: newVersion,
      file_path:      path,
      file_size:      file.size,
      mime_type:      file.type,
      uploaded_by:    userProfile?.id || null,
      change_notes:   versionNotes || null,
    });
  };

  const toggleArchive = async (doc) => {
    const { data } = await updateDocument(doc.id, { is_archived: !doc.is_archived });
    if (data) {
      handleDocUpdate(data);
      if (previewDoc?.id === doc.id) setPreviewDoc(null);
    }
  };

  // Multi-select
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const selectAll = () => {
    setSelectedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((d) => d.id))
    );
  };

  // Bulk actions
  const handleBulkDeleteConfirm = async () => {
    for (const id of selectedIds) {
      const doc = documents.find((d) => d.id === id);
      if (doc) { await deleteDocumentFile(doc.file_path); await deleteDocument(doc.id); }
    }
    setDocuments((prev) => prev.filter((d) => !selectedIds.has(d.id)));
    setSelectedIds(new Set());
    setBulkDeleteCount(0);
  };

  const handleBulkDelete = () => {
    setBulkDeleteCount(selectedIds.size);
  };

  const handleBulkPin = async () => {
    const anyUnpinned = [...selectedIds].some((id) => !documents.find((d) => d.id === id)?.is_pinned);
    for (const id of selectedIds) { await updateDocument(id, { is_pinned: anyUnpinned }); }
    const { data } = await listDocuments(firmId);
    setDocuments(data || []);
    setSelectedIds(new Set());
  };

  const handleBulkArchive = async () => {
    for (const id of selectedIds) { await updateDocument(id, { is_archived: true }); }
    setDocuments((prev) => prev.map((d) => selectedIds.has(d.id) ? { ...d, is_archived: true } : d));
    setSelectedIds(new Set());
  };

  const handleBulkMove = async () => {
    for (const id of selectedIds) {
      await updateDocument(id, { folder_id: bulkMoveFolderId || null });
    }
    const { data } = await listDocuments(firmId);
    setDocuments(data || []);
    setSelectedIds(new Set());
    setBulkMoveOpen(false);
    setBulkMoveFolderId('');
  };

  // Drag-to-folder
  const handleDropOnFolder = async (folderId) => {
    if (!draggingDocId) return;
    const { data } = await updateDocument(draggingDocId, { folder_id: folderId || null });
    if (data) setDocuments((prev) => prev.map((d) => d.id === draggingDocId ? { ...d, ...data } : d));
    setDraggingDocId(null);
  };

  // Folder CRUD
  const handleFolderSaved = (f, isEdit) => {
    setFolders((prev) => isEdit ? prev.map((x) => x.id === f.id ? f : x) : [...prev, f]);
    setFolderModal(false);
    setEditingFolder(null);
    setNewSubParentId(null);
    if (!isEdit && f.parent_id) setSelectedFolder(f.parent_id);
  };

  const handleFolderDeleteConfirm = async () => {
    await deleteFolder(deletingFolder.id);
    setFolders((prev) => prev.filter((x) => x.id !== deletingFolder.id));
    if (selectedFolder === deletingFolder.id) setSelectedFolder(null);
    setDocuments((prev) => prev.map((d) => d.folder_id === deletingFolder.id ? { ...d, folder_id: null, folder: null } : d));
    setDeletingFolder(null);
  };

  const handleFolderDelete = (f) => {
    setDeletingFolder(f);
  };

  // Upload
  const onFiles = (files) => {
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length) {
      toast.error(`Files too large (max 50 MB): ${oversized.map((f) => f.name).join(', ')}`);
      files = files.filter((f) => f.size <= MAX_FILE_SIZE);
      if (!files.length) return;
    }
    const meta = {};
    const autoFolder = selectedFolder && !isSmartId(selectedFolder) ? selectedFolder : '';
    files.forEach((f, i) => {
      meta[i] = { ...BLANK_UPLOAD, name: f.name.replace(/\.[^.]+$/, ''), folder_id: autoFolder };
    });
    setPendingFiles(files);
    setFileMeta(meta);
    setUploadProgress({});
    setShowUpload(true);
  };

  const uploadAll = async () => {
    // ── PRE-FLIGHT STORAGE CHECK ───────────────────────────────────
    // Before uploading a single byte to Supabase Storage, see if the
    // total batch will fit under the firm's cap. Server-side trigger
    // still gates each row (migration 143) so the cap is enforced
    // even without this client check — but this saves the user from
    // waiting for a multi-MB upload only to be rejected on INSERT,
    // and prevents the orphan-blob-in-storage class of bug for the
    // common case.
    if (storageInfo && storageInfo.limit_mb != null) {
      const batchBytes = pendingFiles.reduce((s, f) => s + (f.size || 0), 0);
      const batchMb    = Math.ceil(batchBytes / (1024 * 1024));
      if (batchMb > (storageInfo.available_mb || 0)) {
        const fmt = (mb) =>
          mb >= 1024
            ? `${(mb / 1024).toFixed(1)} GB`
            : `${mb} MB`;
        toast.error(
          `Not enough storage: this batch needs ${fmt(batchMb)} but only ${fmt(storageInfo.available_mb || 0)} is available. ` +
          `Delete some documents or ask your administrator to buy more storage.`,
          { duration: 10000 },
        );
        return;  // Abort the entire batch.
      }
    }

    setUploading(true);
    const newDocs = [];
    let hitStorageCap = false;   // toggle once → one toast, not N
    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      const meta = fileMeta[i] || BLANK_UPLOAD;
      setUploadProgress((p) => ({ ...p, [i]: 'uploading' }));
      let uploadedPath = null;
      try {
        const { path, error: uploadErr } = await uploadDocumentFile(firmId, file);
        if (uploadErr) throw uploadErr;
        uploadedPath = path;     // remember so we can clean up on INSERT failure
        const { data: doc, error: dbErr } = await createDocument(firmId, {
          name:            meta.name || file.name,
          description:     meta.description || null,
          matter_id:       meta.matter_id   || null,
          folder_id:       meta.folder_id   || null,
          category:        meta.category    || 'other',
          tags:            parseTags(meta.tags),
          // BUG FIX: classification was being captured by the upload
          // modal (via fileMeta) but never written to the DB, so every
          // upload landed with the column default ('internal') no
          // matter what the user picked. Reading the row back in the
          // list view then "reverted" the chip to Internal. Persisting
          // the field here closes the loop.
          //
          // is_confidential is derived from classification so the two
          // can never drift — the same derivation runs in the upload
          // modal's onChange (line 2693) and the edit modal (line 2555).
          // Belt-and-braces here in case future callers pass classification
          // without remembering to set is_confidential.
          classification:  meta.classification || 'internal',
          is_confidential: meta.is_confidential
            || ['confidential', 'privileged', 'strictly_confidential']
                 .includes(meta.classification || 'internal'),
          portal_visible:  meta.portal_visible  || false,
          file_path:       path,
          file_size:       file.size,
          mime_type:       file.type,
          uploaded_by:     userProfile?.id || null,
        });
        if (dbErr) throw dbErr;
        log('document', doc.id, 'uploaded', `Document "${doc.name}" uploaded`);
        newDocs.push(doc);
        setUploadProgress((p) => ({ ...p, [i]: 'done' }));
      } catch (err) {
        console.error(err);
        setUploadProgress((p) => ({ ...p, [i]: 'error' }));

        // Map the storage-limit error class so we can show a useful
        // toast (instead of just a silent ❌ on the row).
        const mapped = mapStorageLimitError(err);
        if (mapped?.code === 'STORAGE_LIMIT' && !hitStorageCap) {
          hitStorageCap = true;
          toast.error(
            'Storage limit reached. Visit Settings → Team to buy more storage, ' +
            'or delete some existing documents to free space.',
            { duration: 10000 },
          );
        } else if (mapped?.code !== 'STORAGE_LIMIT') {
          // Non-storage error — surface it generically so the user
          // isn't left wondering why the row went red.
          toast.error(`Upload failed (${file.name}): ${err?.message || 'unknown error'}`);
        }

        // ── ORPHAN CLEANUP ──────────────────────────────────────────
        // If the file made it to Supabase Storage but the INSERT
        // failed, delete the orphan blob. The reaper would eventually
        // catch it, but we don't want the customer's bandwidth and
        // billable storage seconds going to waste while they wait.
        if (uploadedPath) {
          try { await deleteDocumentFile(uploadedPath); }
          catch (_cleanupErr) {
            // Best-effort cleanup; don't surface this to the user —
            // they already got the actual error toast.
          }
        }
      }
    }

    // ── REFRESH STORAGE INFO ─────────────────────────────────────────
    // Whether the batch succeeded or hit the cap, the usage number
    // changed. Refetch so the header chip + Settings card show the
    // new value without a manual reload.
    getMyFirmStorageInfo().then(({ data }) => setStorageInfo(data || null));

    setDocuments((prev) => [...newDocs, ...prev]);
    setUploading(false);
    setShowUpload(false);
    setPendingFiles([]);
    setFileMeta({});
  };

  const matterOptions = matters.map((m) => ({ value: m.id, label: `${m.matter_number ? m.matter_number + ' · ' : ''}${m.title}` }));
  const folderOptions = folders.map((f) => ({ value: f.id, label: f.name }));
  const totalSize     = documents.filter((d) => !d.is_archived).reduce((s, d) => s + (d.file_size || 0), 0);
  const activeFolder  = folders.find((f) => f.id === selectedFolder);
  const allSelected   = filtered.length > 0 && selectedIds.size === filtered.length;

  return (
    <div className="space-y-5">
      {/* First-visit tips — auto-dismisses after the user clicks the X.
          Surfaces the security-classification concept the moment they
          land on the Documents page so they don't accidentally upload
          a Privileged memo as Internal. Full explanation lives in the
          Help Center (search "classification") and in the collapsible
          guide on the upload modal. */}
      <FirstVisitTips
        pageKey="documents"
        title="Every document gets a security tier"
        tips={[
          'Five tiers: Public, Internal, Confidential, Privileged, Strictly Confidential — each enforces who can see the file at the database level, not just in the UI.',
          'Default is Internal (firm staff only). Tap the badge on any document to raise or lower the tier.',
          'Need the full breakdown? Open the Help Center (the ? button bottom-right) and search "classification".',
        ]}
      />
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {documents.filter((d) => !d.is_archived).length} files · {formatBytes(totalSize)} used ·{' '}
            <a
              href="/document-templates"
              className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              Templates
            </a>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Storage chip — visible only when a cap is configured.
              Compact form (just the ratio + color); the full card
              lives on Settings → Team for admins to drill into. */}
          {storageInfo && storageInfo.limit_mb != null && (() => {
            const fmt = (mb) =>
              mb >= 1024
                ? `${(mb / 1024).toFixed(mb >= 10240 ? 0 : 1)} GB`
                : `${mb} MB`;
            const tone = storageInfo.is_full
              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
              : storageInfo.is_near_full
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700';
            const pct = storageInfo.limit_mb > 0
              ? Math.round((storageInfo.used_mb / storageInfo.limit_mb) * 100)
              : 0;
            return (
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border whitespace-nowrap ${tone}`}
                title={
                  storageInfo.is_full
                    ? 'Storage is full — uploads will be blocked. Visit Settings → Team to buy more.'
                    : `${pct}% used · ${fmt(storageInfo.available_mb)} available`
                }
              >
                {fmt(storageInfo.used_mb)} / {fmt(storageInfo.limit_mb)} ({pct}%)
              </span>
            );
          })()}
          <Button icon={Upload} onClick={() => setShowUpload(true)}>Upload files</Button>
        </div>
      </div>

      {/* Drop zone */}
      <UploadZone onFiles={onFiles} />

      {/* Main layout — stack vertically on mobile, side-by-side from
          lg up. Without the `flex-col lg:flex-row` the 240px sidebar
          sits next to the content pane on phones and there's not
          enough room for either side to render usefully. */}
      <div className="flex flex-col lg:flex-row gap-5 items-stretch lg:items-start">
        {/* Sidebar */}
        <FolderSidebar
          folders={folders}
          selected={selectedFolder}
          docCounts={docCounts}
          onSelect={(id) => { setSelectedFolder(id); setSelectedIds(new Set()); }}
          onNew={() => { setEditingFolder(null); setNewSubParentId(null); setFolderModal(true); }}
          onNewSub={(parentId) => { setEditingFolder(null); setNewSubParentId(parentId); setFolderModal(true); }}
          onEdit={(f) => { setEditingFolder(f); setNewSubParentId(null); setFolderModal(true); }}
          onDelete={handleFolderDelete}
          onDrop={handleDropOnFolder}
        />

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <BulkActionBar
              count={selectedIds.size}
              onPin={handleBulkPin}
              onArchive={handleBulkArchive}
              onMove={() => setBulkMoveOpen(true)}
              onDelete={handleBulkDelete}
              onClear={() => setSelectedIds(new Set())}
            />
          )}

          <Card>
            {/* Filter bar */}
            <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Search documents…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
              </div>
              <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                options={[{ value: '', label: 'All categories' }, ...CATEGORIES]} />
              {allTags.length > 0 && (
                <Select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
                  options={[{ value: '', label: 'All tags' }, ...allTags.map((t) => ({ value: t, label: t }))]} />
              )}
              <Select value={confidFilter} onChange={(e) => setConfidFilter(e.target.value)}
                options={[{ value: '', label: 'All' }, { value: 'true', label: 'Confidential' }, { value: 'false', label: 'Non-confidential' }]} />
            </div>

            {/* Breadcrumb / smart folder heading */}
            {(activeFolder || isSmartId(selectedFolder)) && (() => {
              if (isSmartId(selectedFolder)) {
                const meta = SMART_META[selectedFolder];
                return (
                  <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800 text-sm">
                    {meta && <meta.Icon size={14} className="text-slate-500" />}
                    <span className="font-medium text-slate-700 dark:text-slate-200">{meta?.label}</span>
                    <ChevronRight size={13} className="text-slate-400" />
                    <span className="text-slate-500">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
                  </div>
                );
              }
              const path = getFolderPath(folders, selectedFolder);
              return (
                <div className="flex items-center gap-1.5 px-5 py-2.5 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800 text-sm flex-wrap">
                  <button onClick={() => setSelectedFolder(null)} className="text-slate-400 hover:text-indigo-600 transition text-xs">All files</button>
                  {path.map((seg, idx) => (
                    <React.Fragment key={seg.id}>
                      <ChevronRight size={12} className="text-slate-300 flex-shrink-0" />
                      <button
                        onClick={() => setSelectedFolder(seg.id)}
                        className={`flex items-center gap-1.5 transition text-xs ${
                          idx === path.length - 1
                            ? 'font-semibold text-slate-800 dark:text-slate-100 cursor-default'
                            : 'text-slate-500 hover:text-indigo-600'
                        }`}
                      >
                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: seg.color }} />
                        {seg.name}
                      </button>
                    </React.Fragment>
                  ))}
                  <ChevronRight size={12} className="text-slate-300 flex-shrink-0" />
                  <span className="text-slate-400 text-xs">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
                </div>
              );
            })()}

            {/* Table */}
            {loading ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>{[...Array(6)].map((_, i) => <SkeletonRow key={i} />)}</tbody>
                </table>
              </div>
            ) : !filtered.length ? (
              documents.length === 0 ? (
                <EmptyState
                  icon={FolderOpen}
                  title="Upload your first document"
                  description="Documents live on the matter — pleadings, exhibits, correspondence, client uploads. You can also share specific files with your client through their portal."
                  action={<Button icon={Upload} onClick={() => setShowUpload(true)}>Upload files</Button>}
                />
              ) : (
                <EmptyState
                  icon={FolderOpen}
                  title={selectedFolder ? 'This folder is empty' : 'No documents match your filters'}
                  description={selectedFolder
                    ? 'Upload files here or pick another folder from the sidebar.'
                    : 'Try clearing the search or filter above.'}
                  action={<Button icon={Upload} onClick={() => setShowUpload(true)}>Upload files</Button>}
                />
              )
            ) : (
              <>
              {/* Mobile card list */}
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((doc) => {
                  const Icon = fileIcon(doc.mime_type);
                  const catLabel = CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category;
                  return (
                    <li
                      key={doc.id}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-slate-50 dark:active:bg-slate-800"
                      onClick={() => setPreviewDoc(doc)}
                    >
                      <Icon size={20} className="text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {[catLabel, doc.matter?.matter_number, formatBytes(doc.file_size)].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button onClick={(e) => { e.stopPropagation(); handleDownload(doc); }} className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"><Download size={14} /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(doc); }} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 size={14} /></button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40">
                    <tr>
                      <th className="w-10 px-4 py-2 text-center">
                        <button
                          onClick={selectAll}
                          className={`w-4 h-4 rounded border-2 inline-flex items-center justify-center transition ${
                            allSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 hover:border-indigo-400'
                          }`}
                        >
                          {allSelected && <X size={9} className="text-white" strokeWidth={3} />}
                        </button>
                      </th>
                      <th className="text-left px-4 py-2 font-medium">Name</th>
                      <th className="text-left px-4 py-2 font-medium">Folder</th>
                      <th className="text-left px-4 py-2 font-medium">Case File</th>
                      <th className="text-left px-4 py-2 font-medium">Category</th>
                      <th className="text-left px-4 py-2 font-medium">Size</th>
                      <th className="text-left px-4 py-2 font-medium">Owner</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filtered.map((doc) => {
                      const Icon = fileIcon(doc.mime_type);
                      const catLabel = CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category;
                      const tags = parseTags(doc.tags);
                      const isSelected = selectedIds.has(doc.id);
                      const isRenaming = renamingId === doc.id;
                      return (
                        <tr
                          key={doc.id}
                          draggable
                          onDragStart={() => setDraggingDocId(doc.id)}
                          onDragEnd={() => setDraggingDocId(null)}
                          onClick={() => !isRenaming && setPreviewDoc(doc)}
                          className={`group cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-indigo-50/60 dark:bg-indigo-900/10'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                          } ${doc.is_archived ? 'opacity-60' : ''}`}
                        >
                          {/* Checkbox */}
                          <td className="w-10 px-4 py-3 text-center" onClick={(e) => toggleSelect(doc.id, e)}>
                            <span className={`w-4 h-4 rounded border-2 inline-flex items-center justify-center transition ${
                              isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 group-hover:border-indigo-400'
                            }`}>
                              {isSelected && <X size={9} className="text-white" strokeWidth={3} />}
                            </span>
                          </td>

                          {/* Name */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Icon size={16} className="text-slate-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  {isRenaming ? (
                                    <input
                                      autoFocus
                                      value={renameVal}
                                      onChange={(e) => setRenameVal(e.target.value)}
                                      onBlur={commitRename}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') commitRename();
                                        if (e.key === 'Escape') setRenamingId(null);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-sm font-medium px-1.5 py-0.5 rounded border border-indigo-400 outline-none bg-white dark:bg-slate-800 dark:text-white w-40"
                                    />
                                  ) : (
                                    <p
                                      className="font-medium truncate max-w-[180px] group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors"
                                      onDoubleClick={(e) => startRename(doc, e)}
                                      title="Double-click to rename"
                                    >
                                      {doc.name}
                                    </p>
                                  )}
                                  {doc.is_pinned       && <Star       size={11} className="text-amber-400 fill-amber-400 flex-shrink-0" />}
                                  {doc.is_on_legal_hold && <Gavel     size={11} className="text-red-600 flex-shrink-0" title="On Legal Hold" />}
                                  {doc.portal_visible   && <Globe     size={11} className="text-emerald-500 flex-shrink-0" title="Shared with client" />}
                                  {doc.is_archived      && <Archive   size={11} className="text-slate-400 flex-shrink-0" title="Archived" />}
                                </div>
                                {doc.description && <p className="text-xs text-slate-400 truncate max-w-[180px]">{doc.description}</p>}
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  <ClassificationBadge value={doc.classification || 'internal'} />
                                  <ReviewBadge value={doc.review_status || 'draft'} />
                                  {doc.is_on_legal_hold && <LegalHoldBadge />}
                                </div>
                                {tags.length > 0 && <TagChips tags={tags} />}
                              </div>
                            </div>
                          </td>

                          {/* Folder */}
                          <td className="px-4 py-3 text-slate-500">
                            {doc.folder ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: doc.folder.color }} />
                                <span className="text-xs">{doc.folder.name}</span>
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>

                          {/* Case file */}
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {doc.matter
                              ? <><span className="font-medium text-slate-700 dark:text-slate-200">{doc.matter.matter_number}</span>{doc.matter.title && ` · ${doc.matter.title}`}</>
                              : '—'}
                          </td>

                          {/* Category */}
                          <td className="px-4 py-3">
                            <Badge tone={CATEGORY_TONE[doc.category] || 'slate'}>{catLabel}</Badge>
                          </td>

                          {/* Size */}
                          <td className="px-4 py-3 tabular-nums text-slate-500">{formatBytes(doc.file_size)}</td>

                          {/* Owner */}
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {doc.uploaded_by ? fullName(doc.uploaded_by) : <span className="text-slate-300">—</span>}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => openEdit(doc, e)}
                                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600"
                                title="Edit details"
                              ><Pencil size={13} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); togglePin(doc); }}
                                className={`p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition ${doc.is_pinned ? 'text-amber-400' : 'text-slate-400 hover:text-amber-400'}`}
                                title={doc.is_pinned ? 'Unpin' : 'Pin to Favorites'}
                              ><Star size={13} fill={doc.is_pinned ? 'currentColor' : 'none'} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600"
                                title="Download"
                              ><Download size={13} /></button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(doc); }}
                                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-red-600"
                                title="Delete"
                              ><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {selectedIds.size > 0 && (
                  <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-400">
                    Tip: Press <kbd className="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 font-mono">Esc</kbd> to deselect all
                  </div>
                )}
              </div>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Preview slide-over */}
      {previewDoc && (
        <PreviewPanel
          doc={previewDoc}
          matters={matters}
          folders={folders}
          onClose={() => setPreviewDoc(null)}
          onUpdate={handleDocUpdate}
          onDelete={(doc) => { handleDelete(doc); setPreviewDoc(null); }}
          onTogglePin={togglePin}
          onToggleArchive={toggleArchive}
          onToggleLegalHold={toggleLegalHold}
          onNewVersion={handleNewVersion}
          currentUserId={userProfile?.id}
        />
      )}

      {/* Folder modal */}
      <FolderModal
        open={folderModal}
        target={editingFolder}
        parentId={newSubParentId}
        parentFolder={newSubParentId ? folders.find((f) => f.id === newSubParentId) : null}
        firmId={firmId}
        onSaved={handleFolderSaved}
        onClose={() => { setFolderModal(false); setEditingFolder(null); setNewSubParentId(null); }}
      />

      {/* Edit document modal */}
      <Modal
        open={!!editDoc}
        onClose={() => setEditDoc(null)}
        title="Edit document"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditDoc(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit || !editForm.name.trim()}>
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Document name *</Label>
            <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select options={CATEGORIES} value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
            </div>
            <div>
              <Label>Folder</Label>
              <Select placeholder="No folder" options={[{ value: '', label: 'None' }, ...folderOptions]} value={editForm.folder_id} onChange={(e) => setEditForm({ ...editForm, folder_id: e.target.value })} />
            </div>
            <div>
              <Label>Case file</Label>
              <Select placeholder="None" options={[{ value: '', label: 'None' }, ...matterOptions]} value={editForm.matter_id} onChange={(e) => setEditForm({ ...editForm, matter_id: e.target.value })} />
            </div>
            <div>
              <Label>Expires</Label>
              <Input type="date" value={editForm.expires_at} onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Tags <span className="text-slate-400 font-normal">(comma-separated)</span></Label>
            <Input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} placeholder="e.g. urgent, privileged, exhibit-A" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>
                Classification
                <HelpTooltip>
                  Controls who can see this document. <strong>Public</strong> &amp; <strong>Internal</strong> are visible to all firm staff. <strong>Confidential</strong>, <strong>Privileged</strong> and <strong>Strictly Confidential</strong> are restricted by role and enforced by the database — not just the UI. Pick the tier that matches the document's sensitivity. See the classification guide in Help for the full table.
                </HelpTooltip>
              </Label>
              <select
                value={editForm.classification || 'internal'}
                onChange={(e) => setEditForm({ ...editForm, classification: e.target.value, is_confidential: ['confidential','privileged','strictly_confidential'].includes(e.target.value) })}
                className="w-full text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                {CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Review status</Label>
              <select
                value={editForm.review_status || 'draft'}
                onChange={(e) => setEditForm({ ...editForm, review_status: e.target.value })}
                className="w-full text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                {REVIEW_STATUSES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          {/* Smart classification hint for edit modal */}
          <ClassificationHint
            category={editForm.category}
            currentClassification={editForm.classification || 'internal'}
            onApply={(val) => setEditForm((f) => ({
              ...f,
              classification: val,
              is_confidential: ['confidential','privileged','strictly_confidential'].includes(val),
              portal_visible: val === 'public' ? true : f.portal_visible,
            }))}
          />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editForm.portal_visible} onChange={(e) => setEditForm({ ...editForm, portal_visible: e.target.checked })} className="rounded" />
              <Globe size={13} className="text-emerald-500" /> Share with client
            </label>
          </div>
        </div>
      </Modal>

      {/* Bulk move modal */}
      <Modal
        open={bulkMoveOpen}
        onClose={() => setBulkMoveOpen(false)}
        title={`Move ${selectedIds.size} file${selectedIds.size !== 1 ? 's' : ''} to…`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBulkMoveOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkMove}>Move</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Select
            value={bulkMoveFolderId}
            onChange={(e) => setBulkMoveFolderId(e.target.value)}
            options={[{ value: '', label: 'No folder (unfiled)' }, ...folderOptions]}
          />
          <p className="text-xs text-slate-400">Files will be moved out of their current folders.</p>
        </div>
      </Modal>

      {/* Upload modal */}
      <Modal
        open={showUpload}
        onClose={() => { if (!uploading) { setShowUpload(false); setPendingFiles([]); setFileMeta({}); } }}
        title="Upload documents"
        size="lg"
        footer={
          pendingFiles.length > 0 ? (
            <>
              <Button variant="ghost" onClick={() => { setShowUpload(false); setPendingFiles([]); setFileMeta({}); }} disabled={uploading}>Cancel</Button>
              <Button loading={uploading} onClick={uploadAll}>Upload {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''}</Button>
            </>
          ) : null
        }
      >
        {pendingFiles.length === 0 ? (
          <UploadZone onFiles={onFiles} />
        ) : (
          <div className="space-y-4">
            {/* Inline collapsible explainer — only renders the expanded
                view if the user actually opens it, so the modal stays
                compact for users who already know what the tiers mean.
                The same component shows the full table in the Help
                Center; same source of truth either way. */}
            <DocumentClassificationGuide variant="compact" />
            {pendingFiles.map((file, i) => {
              const meta = fileMeta[i] || BLANK_UPLOAD;
              const status = uploadProgress[i];
              const Icon = fileIcon(file.type);
              return (
                <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon size={16} className="text-slate-400 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{file.name}</span>
                      <span className="text-xs text-slate-400 flex-shrink-0">{formatBytes(file.size)}</span>
                    </div>
                    {status === 'done'      && <span className="text-xs text-green-600 font-medium">✓ Done</span>}
                    {status === 'error'     && <span className="text-xs text-red-600 font-medium">✗ Error</span>}
                    {status === 'uploading' && <span className="text-xs text-indigo-600 font-medium">Uploading…</span>}
                    {!status && (
                      <button onClick={() => { setPendingFiles((p) => p.filter((_, j) => j !== i)); setFileMeta((m) => { const c = { ...m }; delete c[i]; return c; }); }}>
                        <X size={14} className="text-slate-400 hover:text-red-500" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Document name</Label>
                      <Input value={meta.name} onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, name: e.target.value } }))} placeholder={file.name} />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select options={CATEGORIES} value={meta.category} onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, category: e.target.value } }))} />
                    </div>
                    <div>
                      <Label>Folder</Label>
                      <Select placeholder="No folder" options={[{ value: '', label: 'None' }, ...folderOptions]} value={meta.folder_id} onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, folder_id: e.target.value } }))} />
                    </div>
                    <div>
                      <Label>Case file</Label>
                      <Select placeholder="No case file" options={matterOptions} value={meta.matter_id} onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, matter_id: e.target.value } }))} />
                    </div>
                    <div className="col-span-2">
                      <Label>Tags <span className="text-slate-400 font-normal">(comma-separated)</span></Label>
                      <Input value={meta.tags} onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, tags: e.target.value } }))} placeholder="e.g. urgent, exhibit-A" />
                    </div>
                  </div>
                  {/* Classification + share row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs font-medium text-slate-500 flex items-center">
                        Classification:
                        <HelpTooltip side="center">
                          Controls who can see this document. Pick the tier that matches sensitivity — the database itself enforces who can access each tier, not just the UI. <strong>Internal</strong> is the right default for most firm documents.
                        </HelpTooltip>
                      </label>
                      <select
                        value={meta.classification || 'internal'}
                        onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, classification: e.target.value, is_confidential: ['confidential','privileged','strictly_confidential'].includes(e.target.value) } }))}
                        className="text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      >
                        {CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={meta.portal_visible} onChange={(e) => setFileMeta((m) => ({ ...m, [i]: { ...meta, portal_visible: e.target.checked } }))} className="rounded" />
                      <Globe size={11} className="text-emerald-500" /> Share with client
                    </label>
                  </div>
                  {/* Smart classification hint */}
                  <ClassificationHint
                    category={meta.category}
                    currentClassification={meta.classification || 'internal'}
                    onApply={(val) => setFileMeta((m) => ({
                      ...m,
                      [i]: {
                        ...meta,
                        classification:  val,
                        is_confidential: ['confidential','privileged','strictly_confidential'].includes(val),
                        portal_visible:  val === 'public' ? true : meta.portal_visible,
                      },
                    }))}
                  />
                </div>
              );
            })}
            <button onClick={() => { setPendingFiles([]); setFileMeta({}); }} className="text-xs text-slate-400 hover:text-red-500 transition">
              Clear all and start over
            </button>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        open={!!deletingDoc}
        onClose={() => setDeletingDoc(null)}
        loading={false}
        title="Delete document"
        description={<>Are you sure you want to delete <strong>{deletingDoc?.name}</strong>? This cannot be undone.</>}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmDeleteModal
        open={bulkDeleteCount > 0}
        onClose={() => setBulkDeleteCount(0)}
        loading={false}
        title="Delete documents"
        description={<>Are you sure you want to delete <strong>{bulkDeleteCount} document(s)</strong>? This cannot be undone.</>}
        onConfirm={handleBulkDeleteConfirm}
      />

      <ConfirmDeleteModal
        open={!!deletingFolder}
        onClose={() => setDeletingFolder(null)}
        loading={false}
        title="Delete folder"
        description={<>Are you sure you want to delete the folder <strong>{deletingFolder?.name}</strong>? Documents inside will become unfiled.</>}
        onConfirm={handleFolderDeleteConfirm}
      />
      
    </div>
  );
}
